<template>
  <AppDialog
    ref="expenseModal"
    class="expense-modal"
    :title="editing ? 'Edit expense' : 'New expense'"
    :show="show"
    append-to-body
    :size="'large'"
    @close="closeDialog"
  >
    <!-- eslint-disable vue/no-mutating-props -->
    <div class="col-sm-12 container">
      <ValidationObserver ref="dialogObserver">
        <ValidationProvider
          v-slot="{ errors }"
          name="base"
        >
          <div
            class="form-group"
            :class="{ 'has-error': errors[0] }"
          >
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>

        <div class="row display-flex">
          <div class="col-md-7">
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              name="description"
            >
              <div
                class="form-group description-group"
                :class="{ 'has-error': errors[0] }"
              >
                <label
                  for="description"
                  class="control-label required"
                >
                  Description
                  <abbr title="required">*</abbr>
                </label>

                <AppInput
                  id="description"
                  v-model="expense.description"
                  type="text"
                  class="expense-description"
                  placeholder="Description"
                />
                <span class="help-block">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
            <div class="row">
              <div class="col-md-6">
                <ValidationProvider
                  v-slot="{ errors }"
                  rules="required"
                  name="date"
                >
                  <div
                    class="form-group receipt-date-group"
                    :class="{ 'has-error': errors[0] }"
                  >
                    <label
                      for="date"
                      class="control-label required"
                    >
                      Date
                      <abbr title="required">*</abbr>
                    </label>
                    <AppDatePicker
                      id="date"
                      v-model="expense.receipt_date"
                      class=""
                      :value="expense.receipt_date"
                      :extra-picker-classes="'form-group string required expenses-date-picker'"
                      :type="'daily'"
                      :show-quick-pick="false"
                      :start-week-on="organization.start_week_on"
                    />
                    <span class="help-block">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
              <div class="col-md-6">
                <ValidationProvider
                  v-slot="{ errors }"
                  rules="required|money"
                  name="amount"
                >
                  <div
                    class="form-group amount-group"
                    :class="{ 'has-error': errors[0] }"
                  >
                    <label
                      for="amount"
                      class="control-label required"
                    >
                      Amount
                      <abbr title="required">*</abbr>
                    </label>
                    <div class="input-group col-sm-12">
                      <input
                        id="amount"
                        v-model="expense.amount"
                        type="text"
                        class="numeric decimal form-control"
                        inputmode="numeric"
                        placeholder="Amount"
                      />
                      <span class="input-group-addon">{{ organization.currency.currency }}</span>
                    </div>
                    <span class="help-block">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
            </div>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              name="category"
            >
              <div
                class="form-group category-group"
                :class="{ 'has-error': errors[0] }"
              >
                <label
                  for="category"
                  class="control-label required"
                >
                  Category
                  <abbr title="required">*</abbr>
                </label>
                <AppSelect
                  id="category"
                  v-model="expense.category"
                  class="select-expense-category"
                  :options="selectOptions('category')"
                  :select2-options="select2Options('category')"
                />
                <span class="help-block">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
            <ValidationProvider
              v-if="showProjects"
              v-slot="{ errors }"
              name="project"
            >
              <div
                class="form-group project-group"
                :class="{ 'has-error': errors[0] }"
              >
                <label
                  for="project"
                  class="control-label"
                >
                  {{ organization.project_flavour | projectFlavourLabel }}
                </label>
                <AppSelect
                  v-if="expense.project"
                  id="project"
                  v-model="expense.project.id"
                  class="select-expense-project"
                  :options="selectOptions('project')"
                  :select2-options="select2Options('project')"
                />
                <span class="help-block">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
            <div class="form-group">
              <label
                for="expense-notes"
                class="control-label"
              >
                Notes
              </label>
              <textarea
                id="expense-notes"
                v-model="expense.notes"
                class="form-control text"
                rows="3"
                placeholder="Notes"
              />
            </div>
          </div>
          <div class="col-md-5 right-panel">
            <div
              class="upload-zone"
              :class="{ 'with-action-buttons': hasAttachment }"
              @drop.prevent="addFile"
              @dragover.prevent
            >
              <ValidationProvider
                ref="receiptProvider"
                v-slot="{ errors }"
                name="receipt"
                tag="div"
                class="receipt"
              >
                <img
                  :src="imagePreview"
                  @error="setDefaultIcon()"
                />
                <div
                  v-show="!sendingRequest && !hasAttachment"
                  class="upload-text"
                >
                  <p>Drag and drop your receipt here or browse to upload.</p>
                  <div
                    v-show="!hasAttachment"
                    class="upload-field"
                  >
                    <label class="btn btn-primary btn-transparent btn-browse">
                      Browse files
                      <AppFilePicker
                        ref="filePicker"
                        name="upload"
                        class="upload"
                        :accept="mimeTypes"
                        @input="uploadPreview"
                      />
                    </label>
                  </div>
                  <div class="file-types">
                    Accepted file formats
                    <br />
                    {{ fileTypes }}.
                  </div>
                </div>
                <span class="help-block">{{ errors[0] }}</span>
              </ValidationProvider>
              <div
                v-show="hasAttachment"
                class="action-buttons"
              >
                <div class="row">
                  <div class="col-xs-6 action-btn">
                    <a
                      class="btn btn-transparent btn-xs action-btn-delete"
                      @click="deleteAttachment()"
                    >
                      <i class="hi hi-12 hi-delete-xs" /> Delete
                    </a>
                  </div>
                  <div class="col-xs-6 action-btn">
                    <a
                      :href="expense.receipt && expense.receipt.original_url"
                      class="btn btn-transparent btn-xs action-btn-download"
                    >
                      <i class="hi hi-12 hi-export" /> Download
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ValidationObserver>
      <div class="row mt-5">
        <div class="col-md-6">
          <div class="form-group boolean optional">
            <div class="checkbox">
              <AppCheckbox
                v-model="expense.billable"
                class="form-group boolean optional expenses-billable-checkbox"
              >
                <span>Billable </span>
              </AppCheckbox>
            </div>
          </div>
        </div>
        <div class="col-md-6" />
      </div>
    </div>
    <!-- eslint-enable vue/no-mutating-props -->
    <template slot="footer">
      <AppButton @click="closeDialog">Cancel</AppButton>
      <AppButton
        type="primary"
        :disabled="sendingRequest"
        @click="saveForm()"
      >
        Save
      </AppButton>
    </template>
  </AppDialog>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import { RECEIPT_ICON } from '~/rails_assets';
import * as types from '../../stores/ExpenseStore/types';
import { extend } from 'vee-validate';
import { getProjectFlavourLabel } from '~/utils/project_flavour';

extend('money', {
  validate: (value) => {
    const amount = +value;

    return !isNaN(amount) && amount > 0;
  },
  message: 'is not valid',
});

export default {
  name: 'ExpenseDialog',
  props: {
    show: Boolean,
    editing: {
      type: Boolean,
      default: false,
    },
    expense: Object,
  },
  data() {
    return {
      defaultSelect2Options: {
        allowClear: true,
        multiple: false,
        width: '100%',
      },
      sendingRequest: false,
      uploadedPreview: null,
      defaultIcon: RECEIPT_ICON,
      fileDetails: '',
      imagePreview: RECEIPT_ICON,
      visibleProjects: null,
    };
  },
  computed: {
    ...mapGetters(['showDialogLoading', 'showUploadLoading', 'mimeTypes', 'fileTypes']),
    ...mapState(['organization', 'current_user_id', 'categories']),
    hasAttachment() {
      return (
        (this.expense && this.expense.receipt && this.expense.receipt.original_url) ||
        this.uploadedPreview != null ||
        this.imagePreview !== this.defaultIcon
      );
    },
    projectSelectDefaultSelect() {
      const projectLabel = getProjectFlavourLabel(this.organization.project_flavour);

      return `Select ${projectLabel.toLowerCase()}`;
    },
    showProjects() {
      return this.organization.project_flavour !== 'work_orders';
    },
  },
  watch: {
    showDialogLoading(loading) {
      $(this.$el.querySelector('.modal-dialog')).loadingCurtain(loading ? 'show' : 'hide');
    },
    showUploadLoading(loading) {
      $(this.$el.querySelector('.right-panel')).loadingCurtain(loading ? 'show' : 'hide');
    },
  },
  mounted() {
    if (this.expense && this.expense.receipt && (this.expense.receipt.small_url || this.expense.receipt.original_url)) {
      const receiptUrl = this.expense.receipt.small_url || this.expense.receipt.original_url;

      if (receiptUrl && receiptUrl.match(/\.(jpeg|jpg|gif|png|svg|tiff)/) != null) {
        this.tryToSetPreview(receiptUrl, true);
      }
    }
  },
  methods: {
    closeDialog() {
      this.$emit('close');
    },
    selectOptions(parent) {
      if (parent === 'project') {
        return this.visibleProjectsOnExpense();
      }

      return this.categories.map((category) => ({ id: category.name, text: category.name }));
    },
    select2Options(parent) {
      if (parent === 'project') {
        return { ...this.defaultSelect2Options, ...{ placeholder: this.projectSelectDefaultSelect } };
      }

      return { ...this.defaultSelect2Options, ...{ placeholder: 'Select a category' } };
    },
    addFile(event) {
      const droppedFiles = event.dataTransfer.files;

      if (droppedFiles && !this.hasAttachment) {
        if (droppedFiles.length === 1) {
          this.uploadPreview(droppedFiles[0]);
        } else {
          this.showMessage('Please drag only one file.');
        }
      }
    },
    deleteAttachment() {
      if (confirm('Are you sure you want to delete this receipt?')) {
        this.clearFileInput();
        if (this.uploadedPreview && this.uploadedPreview.file) {
          this.showUploadLoadingSpinner();
          this.resetPreviewData();
          this.resetReceipt();
          this.setDefaultIcon();
          this.hideUploadLoadingSpinner();
        } else {
          this.$store.dispatch('removeReceipt', { id: this.expense.id }).then(
            () => {
              this.resetReceipt();
              this.setDefaultIcon();
              this.removeAttachment();
              this.showMessage('Receipt removed');
            },
            () => {
              this.showMessage('Unexpected error. Please try again.');
            },
          );
        }
      }
    },
    saveForm() {
      if (this.sendingRequest) {
        return;
      }

      this.$refs.dialogObserver.validate().then((result) => {
        if (result) {
          this.sendingRequest = true;

          if (this.expense.id) {
            this.updateExpense();
          } else {
            this.createExpense();
          }
        }
      });
    },
    updateExpense() {
      this.$store
        .dispatch('updateExpense', { id: this.expense.id, params: this.requestParams() })
        .then(this.doneRequest, this.failedRequest)
        .then(this.finishRequest);
    },
    createExpense() {
      this.$store
        .dispatch('createExpense', this.requestParams())
        .then(this.doneRequest, this.failedRequest)
        .then(this.finishRequest);
    },
    requestParams() {
      const param = {
        expense: {
          description: this.expense.description,
          receipt_date: this.expense.receipt_date.format('DD/MM/YYYY'),
          category: this.expense.category,
          project_id: this.expense.project.id || null,
          amount: this.expense.amount,
          notes: this.expense.notes,
          billable: this.expense.billable,
        },
      };

      if (this.uploadedPreview) {
        param.expense.receipt = {
          id: this.uploadedPreview.s3_fields.key.match(/cache\/(.+)$/)[1],
          storage: 'cache',
          metadata: {
            size: this.uploadedPreview.file.size,
            filename: this.uploadedPreview.file.name,
            mime_type: this.uploadedPreview.file.type,
          },
        };
      }

      return param;
    },
    doneRequest(response) {
      if (response.success) {
        if (this.editing) {
          this.showMessage('Expense updated');
        } else {
          this.showMessage('Expense created');
        }
        this.closeDialog();
      } else {
        if (response.errors) {
          this.$refs.dialogObserver.setErrors(response.errors);
        } else if (response.alert) {
          this.showMessage(response.alert);
        } else {
          this.showMessage('Unexpected error. Please try again.');
        }
      }
    },
    failedRequest(_error) {
      this.showMessage('Unexpected error. Please try again.');
    },
    finishRequest() {
      this.sendingRequest = false;
    },
    uploadPreview(file) {
      if (file) {
        this.tryToSetPreviewFromFile(file);
        this.showUploadLoadingSpinner();

        this.$store.dispatch('uploadPreview', file).then(
          (response) => {
            this.fillPreviewData(file, response);
            /* eslint-disable vue/no-mutating-props */
            this.expense.receipt.original_url = response.get_url;
            this.expense.receipt.name = file.filename;
            /* eslint-enable vue/no-mutating-props */
            this.hideUploadLoadingSpinner();
          },
          (jqXHR, textStatus) => {
            const responseXML = jqXHR.responseXML;

            this.clearFileInput();
            this.resetPreviewData();
            this.resetReceipt();
            this.setDefaultIcon();
            this.hideUploadLoadingSpinner();

            if (responseXML && responseXML.documentElement.firstChild.innerHTML === 'EntityTooLarge') {
              this.showMessage('File is too large.');
            } else {
              this.showMessage('Unexpected error. Please try again.');
            }
          },
        );
      }
    },
    setDefaultIcon() {
      this.imagePreview = this.defaultIcon;
    },
    fillPreviewData(file, response) {
      this.uploadedPreview = {
        file,
        s3_fields: response.fields,
      };
    },
    resetPreviewData() {
      this.uploadedPreview = null;
    },
    showUploadLoadingSpinner() {
      this.sendingRequest = true;
      this.$store.commit('SET_UPLOAD_LOADING', true);
    },
    hideUploadLoadingSpinner() {
      this.sendingRequest = false;
      this.$store.commit('SET_UPLOAD_LOADING', false);
    },
    showMessage(message) {
      this.$appMessage(message);
    },
    resetReceipt() {
      // eslint-disable-next-line vue/no-mutating-props
      this.expense.receipt = { original_url: null, name: null, small_url: null };
    },
    removeAttachment() {
      this.$store.commit(types.REMOVE_ATTACHMENT, this.expense);
    },
    clearFileInputErrors() {
      this.$refs.receiptProvider.reset();
    },
    clearFileInput() {
      this.clearFileInputErrors();
      this.$refs.filePicker.reset();
    },
    tryToSetPreviewFromFile(file) {
      if (file.type && file.type.indexOf('image') !== -1) {
        this.tryToSetPreview(URL.createObjectURL(file), false);
      }
    },
    tryToSetPreview(fileOrURL, showLoading) {
      const image = new Image();
      const vm = this;

      if (showLoading) {
        this.showUploadLoadingSpinner();
      }

      image.addEventListener('load', function () {
        vm.imagePreview = image.src;

        if (showLoading) {
          vm.hideUploadLoadingSpinner();
        }
      });

      image.addEventListener('error', function () {
        if (showLoading) {
          vm.hideUploadLoadingSpinner();
        }
      });

      image.src = fileOrURL;
    },
    visibleProjectsOnExpense() {
      const userId = this.editing ? this.expense.user.id : this.current_user_id;

      if (this.visibleProjects === null) {
        this.visibleProjects = this.$store.getters.visibleProjectsForUser(userId);

        // If not in the list, we add the original Project
        if (this.expense.project && this.expense.project.id) {
          if (!this.visibleProjects.find((project) => parseInt(project.id) === parseInt(this.expense.project.id))) {
            this.visibleProjects.push({ id: this.expense.project.id, name: this.expense.project.name });

            // Finally we sort the list alphabetically if needed.
            if (this.visibleProjects.length > 1) {
              this.visibleProjects.sort((a, b) => a.name.localeCompare(b.name));
            }
          }
        }

        this.visibleProjects = this.visibleProjects.map((project) => ({ id: project.id, text: project.name }));
      }

      return this.visibleProjects;
    },
  },
};
</script>
